import type { FieldOption, IntakeDomain } from "../types";
import type { FormCatalogItem, FormConfigVersion } from "./types";

export const FORM_CONFIG_VERSION_ID = "form-config-v2";

const STANDARD_DOMAINS: Array<Exclude<IntakeDomain, "" | "medical">> = ["beauty", "tour", "ticket", "experience", "transport", "telecom", "fnb", "commerce", "partner"];
const DEPOSIT_CONTRACT_TYPES = ["deposit", "deposit_upsell"];

export const CONTRACT_OPTION_IDS = {
  freeReservationUpsell: "option.contract.free_reservation_upsell",
  depositUpsell: "option.contract.deposit_upsell",
  deposit: "option.contract.deposit",
  settlement: "option.contract.settlement",
} as const;

export type ContractOptionId = typeof CONTRACT_OPTION_IDS[keyof typeof CONTRACT_OPTION_IDS];

export const CONTRACT_OPTION_CATALOG: Record<ContractOptionId, FieldOption> = {
  [CONTRACT_OPTION_IDS.freeReservationUpsell]: {
    id: CONTRACT_OPTION_IDS.freeReservationUpsell,
    label: "무료예약 + 업셀링 n%",
    value: "free_reservation_upsell",
    description: "예약 진입 장벽이 낮아 초반 예약 유도에 유리합니다. 단, 노쇼 가능성이 있어 필요 시 방지 방안을 함께 논의합니다.",
  },
  [CONTRACT_OPTION_IDS.depositUpsell]: {
    id: CONTRACT_OPTION_IDS.depositUpsell,
    label: "보증금 결제 + 업셀링 n%",
    value: "deposit_upsell",
    description: "보증금을 받아 노쇼 방지에 유리합니다. 다만 무료예약보다 예약 과정의 허들이 있어, 예약량이 제한적일 수 있습니다.",
  },
  [CONTRACT_OPTION_IDS.deposit]: {
    id: CONTRACT_OPTION_IDS.deposit,
    label: "보증금 결제 방식 (업셀링 아님)",
    value: "deposit",
    description: "예약 시 보증금을 결제하고 방문 후 현장 결제에서 차감하는 방식입니다. 업셀링 수수료 구조가 아닙니다.",
  },
  [CONTRACT_OPTION_IDS.settlement]: {
    id: CONTRACT_OPTION_IDS.settlement,
    label: "판매가 판매 후 정산가 정산 방식",
    value: "settlement",
    description: "고객에게 판매가로 결제를 받고, 이용/판매 완료 후 약정한 정산가 기준으로 정산합니다.",
  },
};

export const MEDICAL_CONTRACT_OPTIONS = contractOptionsFromIds([
  CONTRACT_OPTION_IDS.freeReservationUpsell,
  CONTRACT_OPTION_IDS.depositUpsell,
]);

export const STANDARD_CONTRACT_OPTIONS = contractOptionsFromIds([
  CONTRACT_OPTION_IDS.deposit,
  CONTRACT_OPTION_IDS.settlement,
]);

const MEDICAL_CONTRACT_OPTION_RULE = [CONTRACT_OPTION_IDS.freeReservationUpsell, CONTRACT_OPTION_IDS.depositUpsell];
export const STANDARD_CONTRACT_OPTION_RULE = [CONTRACT_OPTION_IDS.deposit, CONTRACT_OPTION_IDS.settlement];

export const STATIC_CONTRACT_CATALOG: FormCatalogItem[] = [
  // 계약 조건은 별도 협의로 진행한다. 필요한 카테고리만 어드민 rule로 다시 노출한다.
  { id: "step.contract", kind: "step", label: "계약 구조", defaultVisible: false },
  { id: "field.contract.contractType", kind: "field", parentId: "step.contract", fieldPath: "contract.contractType", label: "계약 구조", defaultVisible: true, defaultRequired: true },
  ...Object.entries(CONTRACT_OPTION_CATALOG).map(([id, option]) => ({
    id,
    kind: "option" as const,
    parentId: "field.contract.contractType",
    label: option.label,
    optionValue: option.value,
    description: option.description,
    defaultVisible: true,
  })),
  { id: "field.contract.upsellCommissionRate", kind: "field", parentId: "step.contract", fieldPath: "contract.upsellCommissionRate", label: "업셀링 수수료율", defaultVisible: true },
];

export const FORM_CATALOG: FormCatalogItem[] = STATIC_CONTRACT_CATALOG;

export const DEFAULT_FORM_CONFIG: FormConfigVersion = {
  versionId: FORM_CONFIG_VERSION_ID,
  status: "published",
  catalog: FORM_CATALOG,
  rules: [
    {
      id: "contract-options.standard",
      targetId: "field.contract.contractType",
      targetKind: "field",
      condition: {},
      effect: { allowedOptionIds: STANDARD_CONTRACT_OPTION_RULE, defaultValue: "deposit" },
      reason: "일반 스팟은 비업셀링 보증금 또는 판매가/정산가 방식으로 운영합니다.",
    },
    {
      id: "contract-options.medical",
      targetId: "field.contract.contractType",
      targetKind: "field",
      condition: { domain: "medical" },
      effect: { allowedOptionIds: MEDICAL_CONTRACT_OPTION_RULE, defaultValue: "free_reservation_upsell" },
      reason: "메디컬 카테고리는 무료예약/보증금 + 업셀링 계약만 운영합니다.",
    },
    {
      id: "contract-default.ticket",
      targetId: "field.contract.contractType",
      targetKind: "field",
      condition: { domain: "ticket" },
      effect: { defaultValue: "settlement" },
      reason: "티켓형 상품은 판매가/정산가 방식이 기본입니다.",
    },
    {
      id: "contract-default.commerce",
      targetId: "field.contract.contractType",
      targetKind: "field",
      condition: { domain: "commerce" },
      effect: { defaultValue: "settlement" },
      reason: "커머스 상품은 판매가/정산가 방식이 기본입니다.",
    },
    {
      id: "contract-default.fnb-coupon",
      targetId: "field.contract.contractType",
      targetKind: "field",
      condition: { categoryId: "fnb_coupon" },
      effect: { defaultValue: "settlement" },
      reason: "F&B 쿠폰은 판매가/정산가 방식이 기본입니다.",
    },
    {
      id: "upsell-rate.hide-standard-domains",
      targetId: "field.contract.upsellCommissionRate",
      targetKind: "field",
      condition: { domain: STANDARD_DOMAINS },
      effect: { visible: false },
      reason: "일반 스팟은 업셀링 수수료율 필드를 노출하지 않습니다.",
    },
    {
      id: "deposit-memo.hide-by-default",
      targetId: "field.contract.depositPolicyMemo",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "보증금 계약일 때만 보증금 메모를 받습니다.",
    },
    {
      id: "deposit-memo.show-for-deposit-contracts",
      targetId: "field.contract.depositPolicyMemo",
      targetKind: "field",
      condition: { contractType: DEPOSIT_CONTRACT_TYPES },
      effect: { visible: true },
      priority: 10,
      reason: "보증금 계약에서는 보증금 관련 요청사항을 받습니다.",
    },
    {
      id: "settlement-memo.hide-by-default",
      targetId: "field.contract.settlementMemo",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "판매가/정산가 계약일 때만 정산 메모를 받습니다.",
    },
    {
      id: "settlement-memo.show-for-settlement",
      targetId: "field.contract.settlementMemo",
      targetKind: "field",
      condition: { contractType: "settlement" },
      effect: { visible: true },
      priority: 10,
      reason: "판매가/정산가 계약에서는 정산 관련 특이사항을 받습니다.",
    },
    {
      id: "admin-account-fields.hide-when-not-needed",
      targetId: "field.reservation.adminLoginId",
      targetKind: "field",
      condition: { field: { path: "reservation.adminAccountNeeded", operator: "isFalse" } },
      effect: { visible: false },
      reason: "Admin 계정 생성이 필요하지 않으면 희망 ID를 받지 않습니다.",
    },
    {
      id: "admin-password.hide-when-not-needed",
      targetId: "field.reservation.adminPassword",
      targetKind: "field",
      condition: { field: { path: "reservation.adminAccountNeeded", operator: "isFalse" } },
      effect: { visible: false },
      reason: "Admin 계정 생성이 필요하지 않으면 임시 PW를 받지 않습니다.",
    },
    {
      id: "contact-notification-email-default.hide",
      targetId: "field.reservation.useContactEmailForNotifications",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "담당자 이메일 알림 동의는 운영 기본값으로 처리하고 파트너 입력에서는 숨깁니다.",
    },
    {
      id: "contact-notification-email-extra.hide",
      targetId: "field.reservation.notificationEmails",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "예약 알림 이메일 별도 입력은 필요 시 Admin 설정에서 다시 노출합니다.",
    },
    {
      id: "contact-notification-email-default.show-for-dental",
      targetId: "field.reservation.useContactEmailForNotifications",
      targetKind: "field",
      condition: { categoryId: "medical_dental" },
      effect: { visible: true, required: false },
      priority: 20,
      reason: "치과는 담당자 이메일 예약 알림 동의 여부를 파트너가 확인합니다.",
    },
    {
      id: "contact-notification-email-extra.show-for-dental",
      targetId: "field.reservation.notificationEmails",
      targetKind: "field",
      condition: { categoryId: "medical_dental" },
      effect: { visible: true },
      priority: 20,
      reason: "치과는 담당자 이메일과 다른 예약 알림 이메일을 별도 입력할 수 있습니다.",
    },
    {
      id: "contact-notification-phone-default.hide",
      targetId: "field.reservation.useContactPhoneForAlimtalk",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "담당자 연락처 알림톡 동의는 운영 기본값으로 처리하고 파트너 입력에서는 숨깁니다.",
    },
    {
      id: "contact-notification-phone-extra.hide",
      targetId: "field.reservation.notificationPhones",
      targetKind: "field",
      condition: {},
      effect: { visible: false },
      reason: "알림톡 받을 번호 별도 입력은 필요 시 Admin 설정에서 다시 노출합니다.",
    },
    {
      id: "contact-notification-phone-default.show-for-dental",
      targetId: "field.reservation.useContactPhoneForAlimtalk",
      targetKind: "field",
      condition: { categoryId: "medical_dental" },
      effect: { visible: true, required: false },
      priority: 20,
      reason: "치과는 담당자 연락처 알림톡 동의 여부를 파트너가 확인합니다.",
    },
    {
      id: "contact-notification-phone-extra.show-for-dental",
      targetId: "field.reservation.notificationPhones",
      targetKind: "field",
      condition: { categoryId: "medical_dental" },
      effect: { visible: true },
      priority: 20,
      reason: "치과는 담당자 연락처와 다른 알림톡 번호를 별도 입력할 수 있습니다.",
    },
    {
      id: "tourist-convenience.hide-unless-selected",
      targetId: "field.content.touristConvenienceOptions",
      targetKind: "field",
      condition: { field: { path: "content.sellingPointTags", operator: "notIncludes", value: "관광객 방문 편의" } },
      effect: { visible: false },
      reason: "대표 소구 포인트에서 관광객 방문 편의를 선택했을 때만 상세 항목을 받습니다.",
    },
    {
      id: "target-concern.hide-unless-selected",
      targetId: "field.content.targetCustomerConcernDetail",
      targetKind: "field",
      condition: { field: { path: "content.targetCustomers", operator: "notIncludes", value: "특정 고민이 있는 고객" } },
      effect: { visible: false },
      reason: "추천 타깃에서 특정 고민이 있는 고객을 선택했을 때만 상세 고민을 받습니다.",
    },
    {
      id: "exclusive-benefit.hide-unless-exists",
      targetId: "field.content.creatripExclusiveBenefit",
      targetKind: "field",
      condition: { field: { path: "content.creatripExclusiveBenefitStatus", operator: "notEquals", value: "있음" } },
      effect: { visible: false },
      reason: "단독 혜택이 있을 때만 상세 내용을 받습니다.",
    },
    {
      id: "product-legacy-price.hide",
      targetId: "repeaterField.products.depositOrSettlementPrice",
      targetKind: "repeaterField",
      condition: {},
      effect: { visible: false },
      reason: "상품표는 보증금/정산가 legacy 통합 필드 대신 계약 구조별 가격 컬럼을 사용합니다.",
    },
    {
      id: "product-free-reservation.hide-by-default",
      targetId: "repeaterField.products.freeReservationLabel",
      targetKind: "repeaterField",
      condition: {},
      effect: { visible: false },
      reason: "무료예약 계약일 때만 무료예약 컬럼을 노출합니다.",
    },
    {
      id: "product-free-reservation.show-for-free-upsell",
      targetId: "repeaterField.products.freeReservationLabel",
      targetKind: "repeaterField",
      condition: { contractType: "free_reservation_upsell" },
      effect: { visible: true },
      priority: 10,
      reason: "무료예약 + 업셀링 계약에서는 무료예약 컬럼을 노출합니다.",
    },
    {
      id: "product-deposit-price.hide-by-default",
      targetId: "repeaterField.products.depositAmount",
      targetKind: "repeaterField",
      condition: {},
      effect: { visible: false },
      reason: "보증금 계약일 때만 보증금 컬럼을 노출합니다.",
    },
    {
      id: "product-deposit-price.show-for-deposit-contracts",
      targetId: "repeaterField.products.depositAmount",
      targetKind: "repeaterField",
      condition: { contractType: DEPOSIT_CONTRACT_TYPES },
      effect: { visible: true },
      priority: 10,
      reason: "보증금 계약에서는 상품별 보증금 컬럼을 노출합니다.",
    },
    {
      id: "product-settlement-price.hide-by-default",
      targetId: "repeaterField.products.settlementPrice",
      targetKind: "repeaterField",
      condition: {},
      effect: { visible: false },
      reason: "판매가/정산가 계약일 때만 정산가 컬럼을 노출합니다.",
    },
    {
      id: "product-settlement-price.show-for-settlement",
      targetId: "repeaterField.products.settlementPrice",
      targetKind: "repeaterField",
      condition: { contractType: "settlement" },
      effect: { visible: true },
      priority: 10,
      reason: "판매가/정산가 계약에서는 상품별 정산가 컬럼을 노출합니다.",
    },
    {
      id: "doctors.hide-living-support",
      targetId: "step.doctors",
      targetKind: "step",
      condition: { categoryId: "telecom_living_support" },
      effect: { visible: false },
      reason: "한국살기 상품은 전문가/의료진 소개 문항이 필요하지 않습니다.",
    },
    {
      id: "medical-certificate.optional-standard-domains",
      targetId: "upload.medicalTourismCertificate",
      targetKind: "upload",
      condition: { domain: STANDARD_DOMAINS },
      effect: { required: false, label: "외국인유치의료기관 등록증 사본 (의료기관만 해당)", description: "의료기관이 아니라면 첨부하지 않아도 됩니다." },
      reason: "비의료 카테고리는 외국인유치의료기관 등록증을 필수로 받지 않습니다.",
    },
  ],
};

export function contractOptionsFromIds(optionIds: ContractOptionId[]): FieldOption[] {
  return optionIds.map((optionId) => ({ ...CONTRACT_OPTION_CATALOG[optionId] }));
}

export function getContractOptionIdByValue(value: string): ContractOptionId | null {
  return (Object.keys(CONTRACT_OPTION_CATALOG) as ContractOptionId[]).find((optionId) => CONTRACT_OPTION_CATALOG[optionId].value === value) ?? null;
}
